import { create } from 'zustand';

export const WATCHED_RUNS_STORAGE_KEY = 'dashboard.percy-watched-runs';
const WATCH_TTL_MS = 60 * 60 * 1000;

export type WatchedRun = {
  startedAt: number;
  chatSessionId?: string;
  isLive?: boolean;
};

type WatchedRuns = Record<string, WatchedRun>;

type WatchedRunsState = {
  runs: WatchedRuns;
};

const readStorage = (): WatchedRuns => {
  if (typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(WATCHED_RUNS_STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const runs: WatchedRuns = {};
    for (const [id, entry] of Object.entries(parsed)) {
      const run = entry as Partial<WatchedRun> | null;
      if (!run || typeof run.startedAt !== 'number') continue;
      runs[id] = {
        startedAt: run.startedAt,
        chatSessionId: typeof run.chatSessionId === 'string' ? run.chatSessionId : undefined,
        isLive: typeof run.isLive === 'boolean' ? run.isLive : undefined,
      };
    }
    return runs;
  } catch {
    return {};
  }
};

const writeStorage = (runs: WatchedRuns) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(WATCHED_RUNS_STORAGE_KEY, JSON.stringify(runs));
  } catch {}
};

// Backed by localStorage so a reload mid-run still notifies, and so two tabs can't both notify for
// the same run.
export const useWatchedRunsStore = create<WatchedRunsState>()(() => ({ runs: readStorage() }));

const commit = (runs: WatchedRuns) => {
  writeStorage(runs);
  useWatchedRunsStore.setState({ runs });
};

// A run we never saw finish (the tab closed, the run hung) would otherwise be polled forever.
const withoutExpired = (runs: WatchedRuns, now: number): WatchedRuns => {
  return Object.fromEntries(Object.entries(runs).filter(([, run]) => now - run.startedAt < WATCH_TTL_MS));
};

export const watchRun = (investigationId: string, run: WatchedRun) => {
  const runs = withoutExpired(readStorage(), run.startedAt);
  if (runs[investigationId]) return;
  commit({ ...runs, [investigationId]: run });
};

// Removing before notifying is what makes the notification exactly-once across tabs: a second tab
// polling the same run finds the id already gone and stays quiet.
export const claimRun = (investigationId: string): WatchedRun | undefined => {
  const runs = readStorage();
  const claimed = runs[investigationId];
  if (!claimed) return undefined;
  const { [investigationId]: _taken, ...rest } = runs;
  commit(rest);
  return claimed;
};

export const forgetRun = (investigationId: string) => {
  claimRun(investigationId);
};

export const pruneWatchedRuns = (now: number) => {
  const runs = readStorage();
  const fresh = withoutExpired(runs, now);
  if (Object.keys(fresh).length === Object.keys(runs).length) return;
  commit(fresh);
};

export const syncWatchedRunsFromStorage = () => {
  useWatchedRunsStore.setState({ runs: readStorage() });
};

export const clearWatchedRuns = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(WATCHED_RUNS_STORAGE_KEY);
    } catch {}
  }
  useWatchedRunsStore.setState({ runs: {} });
};

export const useWatchedRuns = (): (WatchedRun & { id: string })[] => {
  const runs = useWatchedRunsStore(state => state.runs);
  return Object.keys(runs)
    .sort()
    .map(id => ({ id, ...runs[id] }));
};
